import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { ChevronDown, Clock, Radio, FileText, Target, Eye, CheckCircle, X } from 'lucide-react'

// PlaySheet — vertical timeline of queued bounties + scheduled deployments
// for the current event. Surfaces "what's coming next" so a GM running a
// live event can rehearse the order of operations and deploy ahead-of-time
// when the moment is right.
//
// Item shape (after merge):
//   { id, kind: 'scheduled' | 'bounty', when: ISO string, name, points,
//     factionId, factionName, factionColor, status, bounty_id?, deployed? }
//
// Co-GM scoping is honored by callers — GameEvent passes already-scoped
// `bounties`, `factions`, `mcsNodes`. The component itself adds NO
// permission logic beyond the play_sheet_mode 'limited' truncation
// (next-only) and 'off' (renders empty state).

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)' },
  btnPrimary: { fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000', border: 'none', padding: '4px 12px', cursor: 'pointer', boxShadow: '0 0 6px #00ff66', display: 'inline-flex', alignItems: 'center', gap: 6 },
  btnSecondary: { fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'rgba(0, 20, 8, .4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '4px 12px', cursor: 'pointer' },
  amber: '#ffcc33', red: '#ff2a2a', green: '#9aff9a', dim: '#4aaa5a', deep: '#2a6a3a', blue: '#4da0ff',
}

const KIND_ICON = {
  scheduled: Clock,
  frago:     FileText,
  bounty:    Target,
  intel:     Eye,
}

const KIND_COLOR = {
  scheduled: C.blue,
  frago:     C.amber,
  bounty:    '#00ff66',
  intel:     C.amber,
}

const KIND_LABEL = {
  scheduled: 'SCHEDULED',
  frago:     'FRAGO',
  bounty:    'BOUNTY',
  intel:     'INTEL',
}

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' }) : '—'

export default function PlaySheet({
  event,
  bounties = [],
  factions = [],
  mcsNodes = [],
  isGm,
  isCoGm,
  coGmFactionId = null,
  playSheetMode = 'all',  // 'all' | 'limited' | 'off' — for co-GMs
  hideScores = false,
  onRefresh,
}) {
  const navigate = useNavigate()
  const [scheduled, setScheduled] = useState([])
  // intel_triggers carry `linked_bounty_id` for bounties that fire only on
  // intel discovery. Those queued bounties are "planted and waiting" — they
  // don't belong on the play-sheet timeline because they have no schedule
  // and only fire reactively (Bug 26). We hydrate the linked-bounty set
  // alongside scheduled_deployments and use it to filter the queued-bounty
  // merge below.
  const [intelLinkedBountyIds, setIntelLinkedBountyIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(null)  // id of item currently deploying
  const [expandedItems, setExpandedItems] = useState(new Set())

  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  useEffect(() => { fetchScheduled() }, [event?.id])

  const fetchScheduled = async () => {
    if (!event?.id) return
    setLoading(true)
    const [schedRes, intelRes] = await Promise.all([
      supabase
        .from('scheduled_deployments')
        .select('*, bounty:bounties(id, title, mission_name, points, status, bounty_type, category, target_faction_id, target:users!bounties_target_id_fkey(callsign, display_name))')
        .eq('event_id', event.id)
        .order('deploy_at', { ascending: true }),
      supabase
        .from('intel_triggers')
        .select('linked_bounty_id, status')
        .eq('event_id', event.id)
        .not('linked_bounty_id', 'is', null),
    ])
    setScheduled(schedRes.data || [])
    // Only "planted-and-waiting" intel — hidden/queued. Once intel is
    // 'found' / 'spent', its linked bounty has already advanced and the
    // play sheet can render the activated bounty normally.
    const ids = new Set(
      (intelRes.data || [])
        .filter(t => ['hidden','queued'].includes(t.status))
        .map(t => t.linked_bounty_id)
    )
    setIntelLinkedBountyIds(ids)
    setLoading(false)
  }

  // Co-GMs are off the play sheet entirely under 'off'.
  if (isCoGm && playSheetMode === 'off') {
    return (
      <p className="text-center py-8" style={{ ...C.mono, fontSize: 12, color: C.deep }}>
        Play sheet hidden by org policy.
      </p>
    )
  }

  // Build merged timeline.
  const factionMap = useMemo(() => {
    const m = {}
    ;(factions || []).forEach(f => { m[f.id] = f })
    return m
  }, [factions])
  const nodeFaction = useMemo(() => {
    const m = {}
    ;(mcsNodes || []).forEach(n => { m[n.id] = n.faction_id })
    return m
  }, [mcsNodes])

  const items = useMemo(() => {
    const out = []
    // Scheduled deployments — show every row regardless of status; the UI
    // distinguishes deployed vs upcoming visually.
    ;(scheduled || []).forEach(s => {
      const factionId = s.target_faction_id || nodeFaction[s.target_node_id] || s.bounty?.target_faction_id || null
      out.push({
        id: `sched-${s.id}`,
        sched_id: s.id,
        bounty_id: s.linked_bounty_id,
        kind: s.bounty?.category === 'frago' ? 'frago' : 'scheduled',
        when: s.deploy_at,
        name: `${s.bounty?.title || 'KILL'} — ${s.bounty?.target?.callsign || s.bounty?.target?.display_name || `B${(s.bounty?.id || '???').slice(0,3).toUpperCase()}`}`,
        points: s.bounty?.points || 0,
        factionId,
        deployed: !!s.deployed,
        target_type: s.target_type,
      })
    })
    // Live bounties on the field — anything that isn't already represented
    // by a scheduled deployment. We dedupe by bounty_id so a bounty linked
    // to a scheduled row only appears once (as the scheduled row).
    // Bug 59 — queued bounties leaked hidden game data into the play sheet.
    // Only active + awaiting_delegation appear; queued / cancelled / paused
    // sit out until they activate.
    const linkedBountyIds = new Set((scheduled || []).map(s => s.linked_bounty_id).filter(Boolean))
    ;(bounties || [])
      .filter(b =>
        (b.status === 'active' || b.status === 'awaiting_delegation')
        && !linkedBountyIds.has(b.id)
        // Intel-linked planted-and-waiting bounties have no timeline slot —
        // they fire on discovery, not on a clock. Bug 26.
        && !intelLinkedBountyIds.has(b.id)
      )
      .forEach(b => {
        out.push({
          id: `b-${b.id}`,
          bounty_id: b.id,
          kind: b.category === 'frago' ? 'frago' : 'bounty',
          when: b.issued_at || b.created_at,
          name: `${b.title || 'KILL'} — ${b.target?.callsign || b.target?.display_name || `B${b.id.slice(0,3).toUpperCase()}`}`,
          points: b.points || 0,
          factionId: b.target_faction_id,
          deployed: false,
          target_type: 'live',
        })
      })
    // Sort ascending by when
    out.sort((a, b) => (new Date(a.when) - new Date(b.when)))
    // Co-GM faction filter
    let scoped = out
    if (isCoGm) {
      scoped = out.filter(it => !it.factionId || it.factionId === coGmFactionId)
      if (playSheetMode === 'limited') {
        const next = scoped.find(it => !it.deployed)
        scoped = next ? [next] : []
      }
    }
    return scoped
  }, [scheduled, bounties, isCoGm, coGmFactionId, playSheetMode, nodeFaction, intelLinkedBountyIds])

  // NOW indicator — only meaningful while the event is live.
  const now = new Date()
  const isLive = event?.status === 'live'
  const nowSlot = isLive
    ? items.findIndex(it => new Date(it.when) > now)
    : -1

  const deployNow = async (item) => {
    if (!item.sched_id || !item.bounty_id) return
    if (!window.confirm(`Deploy "${item.name}" now?`)) return
    setActing(item.id)
    try {
      const newStatus = item.target_type === 'cadre_queue' ? 'awaiting_delegation' : 'active'
      await supabase.from('bounties').update({ status: newStatus, phase: 'active' }).eq('id', item.bounty_id)
      await supabase.from('scheduled_deployments').update({ deployed: true, deployed_at: new Date().toISOString() }).eq('id', item.sched_id)
      try {
        await supabase.from('mission_events').insert({
          bounty_id: item.bounty_id,
          event_type: 'scheduled_deployment',
          triggered_by: (await supabase.auth.getUser()).data?.user?.id || null,
          faction_id: item.factionId,
          notes: 'Manually deployed via play sheet',
        })
      } catch { /* non-blocking */ }
      await fetchScheduled()
      onRefresh && onRefresh()
    } finally {
      setActing(null)
    }
  }

  if (loading) return (
    <p className="text-center py-8" style={{ ...C.mono, fontSize: 12, color: C.deep }}>Loading play sheet...</p>
  )

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock size={32} style={{ margin: '0 auto', opacity: 0.5, color: C.deep }} />
        <p className="mt-3" style={{ ...C.display, fontSize: 14, color: C.dim }}>
          {isCoGm && playSheetMode === 'limited' ? 'NO UPCOMING ITEM FOR YOUR FACTION' : 'NO QUEUED ITEMS'}
        </p>
        <p className="mt-1" style={{ ...C.mono, fontSize: 11, color: C.deep }}>
          Queue bounties / FRAGOs or schedule deployments to populate the play sheet.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 style={{ ...C.display, fontSize: '16px', color: '#00ff66' }}>
          PLAY SHEET ({items.length})
        </h2>
        {isLive && (
          <span style={{ ...C.mono, fontSize: 10, color: C.amber, letterSpacing: '0.14em', textTransform: 'uppercase', animation: 'pulse 1.6s ease-in-out infinite' }}>
            ● LIVE — NOW
          </span>
        )}
      </div>
      <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } } .ps-active { animation: pulse 2s ease-in-out infinite; }`}</style>

      <div style={{ position: 'relative', paddingLeft: 24 }}>
        {/* Spine */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 8, width: 2, background: 'rgba(0,255,102,.18)' }} />

        {items.map((it, i) => {
          const Icon = KIND_ICON[it.kind] || Clock
          const color = KIND_COLOR[it.kind] || C.dim
          const isPast = !!it.deployed
          const fac = factionMap[it.factionId]
          const isNext = i === nowSlot
          const showNowMarker = isLive && nowSlot === i

          const isExpanded = expandedItems.has(it.id)
          return (
            <div key={it.id} style={{ position: 'relative', marginBottom: 10 }}>
              {showNowMarker && (
                <div style={{
                  position: 'absolute', left: -22, top: -6,
                  width: 18, height: 2, background: C.amber, boxShadow: `0 0 6px ${C.amber}`,
                }} />
              )}
              {/* Spine dot */}
              <div style={{
                position: 'absolute', left: -22, top: 12,
                width: 14, height: 14, borderRadius: '50%',
                background: isPast ? 'rgba(154,255,154,.5)' : color,
                border: `2px solid ${isPast ? '#3a6a3a' : color}`,
                boxShadow: !isPast ? `0 0 6px ${color}` : 'none',
              }} className={!isPast && isNext ? 'ps-active' : ''} />

              <div style={{
                ...C.panel,
                borderColor: isPast ? 'rgba(0,255,102,.08)' : `${color}55`,
                opacity: isPast ? 0.55 : 1,
              }}>
                {/* Clickable header row */}
                <div
                  className="p-3 flex items-center justify-between gap-3"
                  onClick={() => toggleExpand(it.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon size={12} style={{ color, flexShrink: 0 }} />
                      <span style={{ ...C.mono, fontSize: 10, letterSpacing: '0.12em', color, textTransform: 'uppercase' }}>
                        {KIND_LABEL[it.kind]}
                      </span>
                      {fac && (
                        <span style={{ ...C.mono, fontSize: 10, color: fac.color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          · {fac.name}
                        </span>
                      )}
                      {!hideScores && it.points > 0 && (
                        <span style={{ ...C.mono, fontSize: 10, color: C.amber, letterSpacing: '0.08em' }}>· {it.points} PTS</span>
                      )}
                    </div>
                    <p style={{ ...C.display, fontSize: 14, color: '#e0e8de', marginTop: 2 }}>{it.name}</p>
                    <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0', marginTop: 2 }}>
                      {it.kind === 'scheduled' || it.kind === 'frago' ? 'Deploy at ' : 'Queued '}{fmtTime(it.when)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isPast ? (
                      <span style={{ ...C.mono, fontSize: 10, color: C.green, display: 'inline-flex', alignItems: 'center', gap: 4, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        <CheckCircle size={12} /> Deployed
                      </span>
                    ) : it.sched_id && isGm ? (
                      <button onClick={(e) => { e.stopPropagation(); deployNow(it) }} disabled={acting === it.id}
                        style={{ ...C.btnPrimary, opacity: acting === it.id ? 0.5 : 1 }}>
                        <Radio size={11} /> {acting === it.id ? 'DEPLOYING...' : 'DEPLOY NOW'}
                      </button>
                    ) : null}
                    <ChevronDown size={12} style={{ color: C.dim, flexShrink: 0, transform: isExpanded ? 'none' : 'rotate(-90deg)', transition: 'transform .2s' }} />
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid rgba(0,255,102,.12)', padding: '6px 12px 8px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {(it.kind === 'frago' || it.kind === 'bounty') && it.bounty_id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.id}/dossiers`) }}
                        style={{ ...C.btnSecondary, fontSize: 10, padding: '3px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <FileText size={10} /> DOSSIER →
                      </button>
                    )}
                    {it.target_type && it.target_type !== 'live' && (
                      <span style={{ ...C.mono, fontSize: 10, color: '#9aa6b0' }}>
                        target: <span style={{ color: C.dim, textTransform: 'uppercase' }}>{it.target_type.replace(/_/g, ' ')}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
